import { ToolBarCategory } from '../models/category-tool-bar.model';

import { Category } from '@core/domain/models/category.models';
import {
    checkAttachmentName, findCategoryById, getFreeTextAttachment,
} from '@features/category/application/services/category.utils';
import { CategoryFilterState, ProductFilterItem } from '@pages/category';
import { MenuCategory } from '@shared/header/models/category.model';
import { AttachmentsEnums } from '@shared/header/services/attachments.enums';

export function getSiblingCategories(menuCategory: ToolBarCategory): MenuCategory[] {
    const parentCategorySubcategories = findCategoryById(
        menuCategory.categories,
        menuCategory.currentCategory.parentId
    ).subcategories;

    if (!parentCategorySubcategories) return [];

    const shouldIncludeCategory = (category: MenuCategory) => {
        const isType22Or23 = category.type === '22' || category.type === '23';
        const isCategoryHidden = category.isMenuHidden === 'global' || category.isMenuHidden === 'toolbar';
        const doesNotHaveGaudiAttachment = !checkAttachmentName(category, AttachmentsEnums.GAUDI);

        return isType22Or23 && !isCategoryHidden && doesNotHaveGaudiAttachment;
    };

    return parentCategorySubcategories.filter(shouldIncludeCategory);
}

export function getSubcategoriesAsFilters(categoryFilterType: string, filters: CategoryFilterState): ProductFilterItem[] {
    return filters?.filters[categoryFilterType] || [];
}

export function searchSameLevelCategories(
    categories: MenuCategory[],
    locationPath: string,
    shortDescription: string,
    maxDeep: number
): MenuCategory[] {
    if (maxDeep < 0) return [];

    for (const category of categories) {
        if (locationPath === category.locationPath || shortDescription && shortDescription === category.shortDescription) {
            return categories;
        }

        const foundCategories = searchSameLevelCategories(
            category.subcategories,
            locationPath,
            shortDescription,
            maxDeep - 1
        );

        if (foundCategories.length) return foundCategories;
    }

    return [];
}

export function SetCategoryNameUseCase(
    currentCategory: Category | MenuCategory,
    abTestCategory: MenuCategory,
    renameCms: string
): string {
    return currentCategory?.seoCategory?.keywords
        || renameCms
        || (abTestCategory?.level === 3 ? abTestCategory?.parentName : abTestCategory?.name) || currentCategory.name;
}

export function GetFiltersAsSubcategoriesUseCase(filterTypeUsed: string, filters: CategoryFilterState): ProductFilterItem[] {
    if (!filterTypeUsed) return [];
    const filtersAsSubcategories = getSubcategoriesAsFilters(filterTypeUsed, filters);
    return SortCategoryFilterByNameUseCase(filtersAsSubcategories);
}

export function SortCategoryFilterByNameUseCase(filterArr: ProductFilterItem[]): ProductFilterItem[] {
    return filterArr.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

export function CheckIfCategoryIsNewUseCase(category: MenuCategory): boolean {
    return !!category?.attachments?.find(attachment => attachment?.path === 'NEW');
}

export function GetSiblingsUseCase(menuCategory: ToolBarCategory, currentCategoryFromABTest: MenuCategory): MenuCategory[] {
    const parentCategory = findCategoryById(menuCategory?.categories, currentCategoryFromABTest.parentId);

    const showSisters = menuCategory?.currentCategory?.attachments?.some(item => item.name === 'SHOW_SISTERS');
    if (showSisters || menuCategory?.currentCategory?.level > 2 || parentCategory?.level === 1 && parentCategory?.type === '22') {
        return getSiblingCategories(menuCategory).map(category => ({
            ...category,
            freeTextAttachments: getFreeTextAttachment(category),
        }));
    }
    return [];
}

export function SetCurrentCategoryNameUseCase(cat: ToolBarCategory): MenuCategory {
    const currentSubCategory = cat.subCategories?.find(subCategory => subCategory.id === cat.currentCategory.id);

    return {
        ...cat.currentCategory,
        name: currentSubCategory?.name ?? cat.currentCategory?.name,
        className: currentSubCategory?.className ?? cat.currentCategory?.className,
        seoCategory: cat.currentCategory?.seoCategory?.mainHeader === cat.currentCategory?.name ? {
            ...cat.currentCategory?.seoCategory,
            mainHeader: currentSubCategory?.name,
        } : cat.currentCategory?.seoCategory,
    };
}

export function SetFilterVisibilityUseCase(currentCategory: MenuCategory, isSpecialCollection = false): boolean {
    const hasAttachment = (name: string) => currentCategory.attachments?.some(item => item.name === name);
    const isLookbook = currentCategory.metadata?.type === 'lookbook';

    let showGridViewSelector: boolean;
    let showFilter: boolean;

    if (isSpecialCollection) {
        const shouldHide = hasAttachment('hide_gridview_selector') || hasAttachment('hide_filters') || isLookbook;
        showGridViewSelector = !shouldHide;
        showFilter = !shouldHide;
    } else {
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        showGridViewSelector = !hasAttachment('hide_gridview_selector') && !isLookbook;
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        showFilter = !hasAttachment('hide_filters') && !isLookbook;
    }

    const showFilters = showFilter || showGridViewSelector;

    return showFilters;
}

export function GetSiblingCategoriesSpecialCollectionUseCase(
    categories: MenuCategory[],
    category: MenuCategory
): MenuCategory[] {
    const parent = categories.find(c => c.id === category.parentId);

    const sameLevelCategories = categories.filter(c => c.parentId === parent.id);

    if (!sameLevelCategories.length) return [];

    const filterCondition = (c: MenuCategory) => {
        return (c.type === '45' || c.type === '22' || c.type === '1' || category.type === '22' && category.locationPath === c.locationPath) && c.isMenuHidden !== 'global';
    };

    return sameLevelCategories.filter(filterCondition);
}

export function getAllSubcategories(categoryId: string, categories: MenuCategory[], levels?: number): MenuCategory[] {
    const result = [];

    const findSubcategories = (currentCategoryId: string, currentLevel: number) => {
        const category = categories.find(c => c.id === currentCategoryId);
        if (!category?.subCategoryIds || category.subCategoryIds.length === 0) return;

        if (levels !== undefined && currentLevel >= levels) return;

        for (const subCategoryId of category.subCategoryIds) {
            const subCategory = categories.find(c => c.id === subCategoryId);
            if (subCategory) {
                result.push(subCategory);
                findSubcategories(subCategory.id, currentLevel + 1);
            }
        }
    };

    findSubcategories(categoryId, 1);
    return result;
}
